import {
  useState,
  useRef,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  ButtonWrapper,
  HorizondalLine,
  MenuWrapper,
  SeconadaryButton,
} from "./UploadButtonStyled";
import { useOutsideClick } from "../../utils/useOutsideClick"; // Import the hook
import {
  CaretDown,
  CaretRight,
  FileArrowUp,
  FilePlus,
  FolderSimplePlus,
  Brain,
} from "@phosphor-icons/react";
import useAuth from "../../hooks/useAuth";
import { WordIcon, PdfIcon, ExcelIcon } from "../icons/FileTypeIcons";
import { useDispatch, useSelector } from "react-redux";
import {
  createFolders,
  fetchMatterDetail,
  fetchMatterDetailList,
  fetchMatterDetailsFolder,
  fetchTemplateFields,
  mergeTemplateFields,
  previewTemplateFields,
  uploadTemplate,
  setTemplateFields,
  setMergedDocument,
  setDocListingPage,
  uploadMatterDetails,
  postHighlightedMatter,
} from "../../slice/MatterDetailsSlice";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { appendAIPaneTo } from "../../utils/aiPaneUrl";
import PropTypes from "prop-types";
import ErrorToast from "../Notification/ErrorToast";
import { ConfirmationIcon, UploadFoldersIcon } from "../../assets/icons";
import {
  getNextUntitledFileName,
  getNextUntitledFolderName,
} from "../../utils/nextAvailableName";
import { UPLOAD_PICKER_ACCEPT } from "../../utils/constants";
import SelectTemplatePopup from "./uploadButtonModals/SelectTemplatePopup"; // Adjust path as needed
import FillFormFieldsModal from "./uploadButtonModals/FillFormFieldsModal"; // Adjust path as needed
import GeneratingLoader from "./uploadButtonModals/GeneratingLoader";
import { UploadProgress, MessageModal, ConfirmModal } from "../common/Modals";
import TemplateIcon from "../../assets/images/Template.png";
import Loader from "../../components/Loader/Loader";
import { Box } from "@mui/material";
import { createObjectURL, extractZipsForUpload, getUploadMatterLabel, isZipFile } from "../../utils/uploadUtils";
import { AI_ZIP_KIND, buildResume } from "../../utils/uploadTrayStorage";
import {
  SERVER_SIDE_ZIP_THRESHOLD_BYTES,
  pollAiZipJob,
  summarizeAiZipJob,
  uploadZipServerSide,
  zipJobTrayFields,
} from "../../service/aiZipUploadService";
import { useUploadNotification } from "../../context/UploadNotificationContext";
import { resolveMatterId } from "../../utils/matterIdentity";
import { animateProgress } from "../../utils/commonHandlers";
import { handleFormFillModalClose, handleGeneratedSuccessClose, handleGoBackToFillForm, handleCancelAction, handleShowConfirmModal, handlePreviewMergingTemplate, onTemplateNext, mergeTemplate, openFile, findFolderNode, getFileNamesInFolder, getFolderNamesInFolder, mergePackageFlow } from "./UploadButtonUtils";
import { mergePackage as mergePackageApi } from "../../service/settings";
import PackageMergeSuccessModal from "./uploadButtonModals/PackageMergeSuccessModal";
import AIUploadDestinationModal from "./AIUploadDestinationModal";
import { getDefaultDestinationKey, mergeFolderDestinationTree } from "./aiUploadDestination";

//Recursively traverses a FileSystemDirectoryHandle (from showDirectoryPicker).
const traverseDirectoryHandle = async (dirHandle, path, allFiles) => {
  const childPath = path ? `${path}${dirHandle.name}/` : `${dirHandle.name}/`;
  let isEmpty = true;

  for await (const [name, handle] of dirHandle) {
    isEmpty = false;
    if (handle.kind === 'file') {
      const file = await handle.getFile();
      file.customRelativePath = childPath + name;
      allFiles.push(file);
    } else if (handle.kind === 'directory') {
      await traverseDirectoryHandle(handle, childPath, allFiles);
    }
  }

  if (isEmpty) {
    allFiles.push({ file: null, customRelativePath: childPath, emptyFolderName: dirHandle.name });
  }
};

const UploadButton = forwardRef(({
  menuPosition,
  top,
  right,
  width,
  openNewPDF,
  setCurrentPage,
  setEditingKey,
  setEditingValue,
  setisLoading,
  treeData,
  setShowNotification,
  setShowENotification,
  handleUpload,
  matterId,
}, ref) => {
  
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const menuRef = useRef(null);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const aiFileInputRef = useRef(null);
  // AI Claim File Upload is capability-gated (2026-08-05), not merely
  // admin-gated — see users/capabilities.py::ai.upload in dms_backend.
  const { hasCapability } = useAuth();
  const canUseAiUpload = hasCapability("ai.upload");
  const {  matterDetails, matterDetailsList, templateFields, mergedDocument } = useSelector((state) => state.matterDetails);

  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorToastMsg, setErrorToastMsg] = useState("")
  const { id } = useParams()
  const [openCreateFile, setOpenCreateFile] = useState(false);
  const [showTemplatePopup, setShowTemplatePopup] = useState(false);
  const [showFillModal, setShowFillModal] = useState(false);
  const [showGeneratedLoader, setShowGeneratedLoader] = useState(false);
  const [showGeneratedSuccess, setShowGeneratedSuccess] = useState(false);
  const [confirmCloseModal, setConfirmCloseModal] = useState(false);
  const [formValues, setFormValues] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedLetterhead, setSelectedLetterhead] = useState(null);
  const [poaAttorney, setPoaAttorney] = useState("");
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [showUploadedFile, setShowUploadedFile] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [packageMergeResult, setPackageMergeResult] = useState(null);
  // AI Upload destination picker — see AIUploadDestinationModal.js. We capture
  // the user's file selection on aiFileInputRef.onChange and stash it here
  // until the user picks a destination folder; only then do we call
  // handleUpload(...). pendingAIUpload is null when the modal is closed.
  const [pendingAIUpload, setPendingAIUpload] = useState(null);
  // Global upload tray — server-side zip extractions report progress here
  // so they get the same progress/cancel UI as regular uploads.
  const { addUpload, updateUpload, removeUpload, addFailedUpload, addUploadAbortController } = useUploadNotification();
  const [templatePreview, setTemplatePreview] = useState({
    file_content: '',
    file_type: "",
    content_type: "",
    filename: ""
  });
const location = useLocation();
  const [openPreview, setOpenPreview] = useState(false);
  useOutsideClick(menuRef, () => setIsMenuOpen(false));
  const aiUploadTreeData = useMemo(
    () => mergeFolderDestinationTree(treeData, matterDetailsList),
    [treeData, matterDetailsList],
  );

  const handleMenuClick = () => {
    setShowTemplatePopup(true);
  };

  // Lets a standalone "Create Doc from Template" button (rendered
  // outside this component, e.g. on the Matter Dashboard) open the same
  // template picker as the "Create file from template" menu item, without
  // duplicating the merge/fill/preview flow that lives in this component.
  // Send Doc for E-Signature: generate a ready-to-sign form through the same
  // fill/merge flow, then hand the new document to the caller.
  const signatureHandoffRef = useRef(null);
  const signatureBackRef = useRef(null);
  const leaveSignatureFlow = () => {
    const goBack = signatureBackRef.current;
    signatureHandoffRef.current = null;
    signatureBackRef.current = null;
    goBack?.();
  };
  // In the signature flow, "back to the picker" means back to the
  // e-signature window, not the general template picker.
  const showPickerOrLeaveSignatureFlow = (show) => {
    if (show && signatureHandoffRef.current) {
      leaveSignatureFlow();
      return;
    }
    setShowTemplatePopup(show);
  };
  useImperativeHandle(ref, () => ({
    openTemplatePicker: () => setShowTemplatePopup(true),
    openFilePicker: () => fileInputRef.current?.click(),
    generateForSignature: (template, { onGenerated, onBack } = {}) => {
      signatureBackRef.current = onBack || null;
      signatureHandoffRef.current = (document) => {
        signatureHandoffRef.current = null;
        signatureBackRef.current = null;
        onGenerated?.(document);
      };
      setSelectedTemplate(template);
      setSelectedLetterhead(null);
      onTemplateNext({
        file: null,
        setShowTemplatePopup: showPickerOrLeaveSignatureFlow,
        setUploadProgress,
        setShowUploadProgress,
        setSelectedTemplate,
        setFormValues,
        setShowFillModal,
        dispatch,
        fetchTemplateFields,
        animateProgress,
        handleError,
        setIsUploadLoading,
        uploadTemplate,
        selectedTemplate: template,
        selectedLetterhead: null,
        setErrorToastMsgAndShow,
        matterDetails,
        setShowGeneratedLoader,
        setShowGeneratedSuccess,
        fetchMatterDetail,
        fetchMatterDetailList,
        fetchMatterDetailsFolder,
        mergeTemplateFields,
        getSelectedKey,
        treeData,
        id,
        navigate,
        postHighlightedMatter,
        onGenerated: signatureHandoffRef.current,
      });
    },
  }));

  const setErrorToastMsgAndShow = (message) => {
    const id = Date.now().toString();
    const newMsg = message;
    setErrorToastMsg([{ id, newMsg }]);
    setShowErrorNotification(true)
  }

  const getMatterItemSegments = () => {
    const matterPath = location.pathname.split("/matter/")[1];
    if (!matterPath) return [];
    const [, ...itemSegments] = matterPath.split("/").filter(Boolean);
    return itemSegments;
  }

  const getSelectedKey = () =>{
    const urlIds = getMatterItemSegments();
    const lastSegment = urlIds.at(-1);
    if (!lastSegment) return null;
    const selectedKey = (!lastSegment.startsWith("F") && !lastSegment.startsWith("D"))?null:lastSegment;
    return selectedKey;
  }

  const getSelectedFolderKey = () => {
    return [...getMatterItemSegments()].reverse().find((segment) => segment.startsWith("F")) || null;
  }

  const getMatterIdForCreateFolder = (currentFolder = null) => {
    return resolveMatterId({
      currentRecord: currentFolder,
      matterDetails,
      explicitMatterId: matterId,
      routeId: id,
      location,
      treeData,
    });
  };

  const AfterUploadMatterDetails = (resCreateFile) => {
     localStorage.setItem("newPdf",true)
     console.log("resCreateFile",resCreateFile)
     setEditingKey(resCreateFile.data.completed_documents[0].unique_id);
     setEditingValue(resCreateFile.data.completed_documents[0].name);
    openNewPDF(resCreateFile.data.completed_documents[0], true);
      setCurrentPage(1)
      dispatch(setDocListingPage(1));
  }
 
  const createNewPDFFile = async () => {
    setShowENotification(false)
    const selectedKey = getSelectedKey();
    const currentFolder = selectedKey ? findFolderNode(treeData, selectedKey) : null;
    const currentFileNames = selectedKey
      ? getFileNamesInFolder(currentFolder)
      : treeData
        .filter(item => item.type === "document")
        .map(doc => doc.title);

    const newFileName = getNextUntitledFileName(currentFileNames);
    const fileUrl = "/Untitled.pdf";
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob(); // Convert response to Blob
      console.log("newFile",newFileName)
      const file = new File([blob], newFileName, { type: blob.type });
      setOpenCreateFile(false);
      setIsMenuOpen(false);
      
      // Use uploadMatterDetails with matterDetails to enable signed URL flow (for PDF only)
      const uploadParams = createObjectURL(file, true, matterDetails, selectedKey);
      dispatch(uploadMatterDetails({ ...uploadParams, matterDetails })).unwrap()
        .then((resCreateFile) => {
          console.log("rescreatedFile",resCreateFile)
          if (resCreateFile.status === 200 || resCreateFile.status === 201) {
            AfterUploadMatterDetails(resCreateFile);
          } else if (resCreateFile.status === 400) {
            const id = Date.now().toString();
            const newMsg = resCreateFile?.payload?.response?.data?.error || resCreateFile?.payload?.response?.data?.detail || "File creation failed";
            setErrorToastMsg([{ id, newMsg }]);
            setShowErrorNotification(true);
          }
        })
        .catch((err) => {
          setErrorToastMsgAndShow(err?.response?.data?.error || err?.response?.data?.detail || "File creation failed");
          setisLoading(false);
        });
    } catch (error) {
      setErrorToastMsgAndShow(error?.response?.data?.error || error?.response?.data?.detail || "File creation failed");
      setisLoading(false);
    }
  };

  const createNewFile = async (fileName = "Untitled.docx") => {
    setShowENotification(false)
    const selectedKey = getSelectedKey();
    const currentFolder = selectedKey ? findFolderNode(treeData, selectedKey) : null;
    const currentFileNames = selectedKey
      ? getFileNamesInFolder(currentFolder)
      : treeData
        .filter(item => item.type === "document")
        .map(doc => doc.title);

    const newFileName = getNextUntitledFileName(currentFileNames, `.${fileName.split('.').pop()}`);
    const fileUrl = `/${fileName}`;

    try {
      const blob = await (await fetch(fileUrl)).blob();
      const file = new File([blob], newFileName, { type: blob.type });

      setOpenCreateFile(false);
      setIsMenuOpen(false);

      // Use legacy uploadMatterDetails (without matterDetails) for new document creation
      const uploadParams = createObjectURL(file, true, matterDetails, selectedKey);
      dispatch(uploadMatterDetails(uploadParams)).unwrap()
        .then((resCreateFile) => {
          if (resCreateFile.status === 201) {
            const url = resCreateFile?.data?.uploaded_documents?.[0]?.sharepoint_web_url;
            const uniqueId = getSelectedKey();
            if (url) globalThis.window.open(url, '_blank', 'noopener,noreferrer');
            if (matterDetails.sf_id) {
              dispatch(fetchMatterDetail(matterDetails.id));
            } else {
              dispatch(fetchMatterDetail(id));
              dispatch(fetchMatterDetailsFolder(matterDetails.id));
            }
            setCurrentPage(1);
            dispatch(setDocListingPage(1));
            if (uniqueId) {
              dispatch(fetchMatterDetailList({ id: id, page: 1, folder: uniqueId }));
            } else {
              dispatch(fetchMatterDetailList({ id: id, page: 1 }));
            }
          } else if (resCreateFile.status === 400) {
            handleError(resCreateFile?.payload?.response?.data?.error || resCreateFile?.payload?.response?.data?.detail);
          }
        })
        .catch((err) => {
          console.error("Upload error:", err);
          handleError(err?.response?.data?.error || err?.response?.data?.detail);
        });
    } catch (error) {
      console.error("Error fetching file:", error);
      handleError(error?.response?.data?.error || error?.response?.data?.detail);
    }
  };

  const handleError = (message = "File creation failed") => {
    const id = Date.now().toString();
    setErrorToastMsg([ { id, newMsg: message }]);
    setShowErrorNotification(true);
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const uniqueId = getSelectedKey();
    setShowENotification(false);

    handleUpload(selectedFiles, uniqueId, null);

    e.target.value = "";
  };

  // AI Upload accepts:
  //   * raw `.pdf` files (treated like a normal AI Upload — flat list)
  //   * `.zip` files containing PDFs (e.g. VBMS claim-file exports). The zip
  //     is unpacked client-side; each zip becomes a top-level folder named
  //     after the zip (without `.zip`) and its internal folder structure is
  //     preserved. Each extracted PDF flows through the same upload + AI
  //     processing pipeline as a directly-picked PDF, so the rest of the
  //     code path doesn't need to know zips exist.
  //
  // Before kicking the upload, we now ask "Upload to: <folder>" via
  // AIUploadDestinationModal — this is the entire reason the matter's
  // "AI Claim File" folder exists; without an explicit destination the
  // backend was placing AI-uploaded PDFs in whatever folder happened to be
  // first in the matter's tree, which is the wrong default for AI flows.
  const handleAIFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    e.target.value = "";
    if (selectedFiles.length === 0) return;

    setShowENotification(false);

    const zips = selectedFiles.filter(isZipFile);
    const directPdfs = selectedFiles.filter((f) => !isZipFile(f));

    if (zips.length === 0) {
      // Flat-list path: array of File objects; no folderNames; no zip extraction.
      const fileCount = directPdfs.length;
      setPendingAIUpload({
        files: directPdfs,
        folderNames: null,
        fileCount,
        serverZips: [],
        // Capture the default destination at modal-open time. Recomputing it
        // on every render would let a background sidebar click silently
        // reset the user's pick (the modal's useEffect re-syncs from the
        // prop whenever it changes).
        defaultFolderKey: getDefaultDestinationKey(aiUploadTreeData, getSelectedKey()),
      });
      return;
    }

    // Big zips can't be unpacked in the browser (single-ArrayBuffer +
    // JSZip ZIP64 limits), so they route to the server-side extraction
    // flow. Small zips still extract client-side for the instant folder
    // preview — but if JSZip chokes on one (e.g. an unusual ZIP64
    // archive under the threshold), it falls back to the server path
    // instead of dead-ending on a misleading "corrupt" error.
    const serverZips = zips.filter((z) => z.size >= SERVER_SIDE_ZIP_THRESHOLD_BYTES);
    const clientZips = zips.filter((z) => z.size < SERVER_SIDE_ZIP_THRESHOLD_BYTES);

    let extracted = { files: [], folderNames: [] };
    if (clientZips.length > 0) {
      try {
        extracted = await extractZipsForUpload(clientZips);
      } catch (err) {
        console.warn("Client-side zip extraction failed; using server-side path", err);
        serverZips.push(...clientZips);
        extracted = { files: [], folderNames: [] };
      }
    }

    if (
      extracted.files.length === 0 &&
      directPdfs.length === 0 &&
      serverZips.length === 0
    ) {
      setErrorToastMsgAndShow("No PDFs found in the selected zip file(s).");
      return;
    }

    // Direct PDFs land at the root of the selected folder, so they don't
    // need a customRelativePath; extracted PDFs already have one.
    const wrappedDirectPdfs = directPdfs.map((file) => ({ file, customRelativePath: null }));
    const allFiles = [...extracted.files, ...wrappedDirectPdfs];

    // Server zips become top-level folders named after the zip, same as
    // client-extracted ones — include them for folder-conflict detection.
    const serverZipFolderNames = serverZips.map((z) => z.name.replace(/\.zip$/i, ""));

    setPendingAIUpload({
      files: allFiles,
      folderNames: [...(extracted.folderNames || []), ...serverZipFolderNames],
      // Count actual file entries, not folder shells (extracted.files may
      // include `{ file: null, ... }` placeholders for empty folders).
      // Server-zip contents are counted after server-side extraction.
      fileCount: allFiles.filter((f) => f && (f.file || f instanceof File)).length,
      serverZips,
      defaultFolderKey: getDefaultDestinationKey(aiUploadTreeData, getSelectedKey()),
    });
  };

  // Called from AIUploadDestinationModal when the user clicks Upload. The
  // chosen `folderKey` is either a folder key string ("Fxxxxx") or null for
  // matter root — both shapes are what handleUpload's `uniqueId` argument
  // already accepts.
  const handleAIDestinationConfirm = (folderKey) => {
    if (!pendingAIUpload) return;
    const { files, folderNames, serverZips } = pendingAIUpload;
    setPendingAIUpload(null);

    // Client-extracted files + direct PDFs go through the existing flow.
    if (files.length > 0) {
      handleUpload(files, folderKey, folderNames, { aiProcess: true });
    }

    // Large / browser-unreadable zips go through the server-side
    // extraction flow; the backend unpacks them and routes every PDF
    // through the same upload + AI pipeline path as the client flow.
    (serverZips || []).forEach((zipFile) => {
      runServerZipUpload(zipFile, folderKey);
    });
  };

  // A failed zip leaves the Uploads list for the Failed tab; left in place
  // it would count as still uploading forever.
  const moveZipToFailed = (uploadId, zipFile, error) => {
    removeUpload(uploadId);
    addFailedUpload({
      id: uploadId,
      fileName: zipFile.name,
      fileSize: zipFile.size,
      type: "ai-zip",
      status: "failed",
      matterId: getMatterIdForCreateFolder() || undefined,
      matterLabel: getUploadMatterLabel(matterDetails) || undefined,
      error,
    });
  };

  // Upload one zip to the server-side extraction flow, reporting progress
  // through the global upload tray, then refresh the matter tree when the
  // backend finishes extracting.
  const runServerZipUpload = async (zipFile, folderKey) => {
    const uploadId = `ai-zip-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const abortController = new AbortController();

    const zipMatterId = getMatterIdForCreateFolder();
    // Modal keys are "Fxxxx"; the API wants the bare folder id.
    const zipFolderId = folderKey ? folderKey.replace(/^F/, "") : null;
    addUpload({
      id: uploadId,
      fileName: zipFile.name,
      matchName: zipFile.name,
      fileSize: zipFile.size,
      progress: 0,
      status: "uploading",
      type: "ai-zip",
      matterId: zipMatterId || undefined,
      matterLabel: getUploadMatterLabel(matterDetails) || undefined,
      // Where the zip is headed, so a reload mid-send can be finished by
      // picking the zip again (see uploadTrayStorage.js).
      resume: buildResume({
        kind: AI_ZIP_KIND,
        matterDetails,
        matterId: zipMatterId,
        folderId: zipFolderId,
        aiProcess: true,
      }),
    });
    addUploadAbortController(uploadId, abortController);

    try {
      const job = await uploadZipServerSide(zipFile, {
        matterId: zipMatterId,
        folderId: zipFolderId,
        signal: abortController.signal,
        onProgress: (percent) =>
          updateUpload(uploadId, { progress: percent, status: "uploading" }),
      });

      // Upload done — extraction now runs server-side. Keep the tray row
      // alive with live extraction counts until the job goes terminal.
      // serverJobId: from here the server owns the work, so a reload only
      // needs to check on the job, not send the zip again.
      updateUpload(uploadId, {
        ...zipJobTrayFields(job),
        progress: 100,
        status: "uploading",
        serverJobId: job.job_id,
      });

      const finalJob = await pollAiZipJob(job.job_id, {
        onUpdate: (j) => updateUpload(uploadId, zipJobTrayFields(j)),
      });

      if (finalJob.status === "failed" || finalJob.status === "aborted") {
        moveZipToFailed(uploadId, zipFile, summarizeAiZipJob(finalJob));
        setErrorToastMsgAndShow(
          `"${zipFile.name}": ${summarizeAiZipJob(finalJob)}`
        );
        return;
      }

      updateUpload(uploadId, {
        ...zipJobTrayFields(finalJob),
        progress: 100,
        status: "completed",
        fileName: zipFile.name,
      });

      // Surface per-file skips/failures (duplicates, invalid names) —
      // silent partial success is how uploads get "lost".
      if (finalJob.status === "completed_with_errors") {
        const preview = (finalJob.errors || [])
          .slice(0, 3)
          .map((e) => `${e.path} (${e.reason})`)
          .join("; ");
        setErrorToastMsgAndShow(
          `"${zipFile.name}": some files were not uploaded — ${preview}` +
            (finalJob.errors?.length > 3 ? ` and ${finalJob.errors.length - 3} more` : "")
        );
      }

      // Refresh the matter tree so the new folders/documents appear.
      dispatch(fetchMatterDetail(id));
      dispatch(fetchMatterDetailsFolder(id));
      dispatch(setDocListingPage(1));
      dispatch(fetchMatterDetailList({ id: id, page: 1 }));
    } catch (err) {
      console.error("Server-side zip upload failed", err);
      moveZipToFailed(
        uploadId,
        zipFile,
        err?.response?.data?.error || "Could not upload this zip. Please try again."
      );
      setErrorToastMsgAndShow(
        `Could not upload "${zipFile.name}". ${
          err?.response?.data?.error || "Please try again."
        }`
      );
    }
  };

  const handleAIDestinationCancel = () => {
    setPendingAIUpload(null);
  };

  // Handle folder selection
  const handleFolderChange = (e) => {
    const selectedFolders = Array.from(e.target.files);
    const uniqueId = getSelectedKey();
    setShowENotification(false);

    const folderNames = [];

    for (const file of selectedFolders) {
      const topFolder = file.webkitRelativePath.split("/")[0];
      if (topFolder && !folderNames.includes(topFolder)) {
        folderNames.push(topFolder);
      }
    }

    handleUpload(selectedFolders, uniqueId, folderNames);

    e.target.value = null;
  };

  const handleUploadFileClick = () => {
    fileInputRef.current.click(); // Trigger file input click when button is clicked
  };

  const handleUploadFolderClick = async () => {
    // Use File System Access API when available — it correctly surfaces empty folders
    // and produces the same traversal structure as drag-and-drop (traverseFileTree),
    // so processFiles' existing emptyFolders logic handles everything automatically.
    if ('showDirectoryPicker' in globalThis.window) {
      try {
        const dirHandle = await globalThis.window.showDirectoryPicker({ mode: 'read' });
        const uniqueId = getSelectedKey();
        setShowENotification(false);

        const allFiles = [];
        await traverseDirectoryHandle(dirHandle, '', allFiles);

        handleUpload(allFiles, uniqueId, [dirHandle.name]);
      } catch (err) {
        // AbortError means the user cancelled the picker — no notification needed.
        if (err.name !== 'AbortError') {
          setErrorToastMsgAndShow('Could not access the folder. Please try again.');
        }
      }
    } else {
      // Fallback for browsers that don't support showDirectoryPicker (e.g. Firefox).
      folderInputRef.current.click();
    }
  };

  const AfterCreateFolder = (resHandleFolderCreation) => {
    setEditingKey(resHandleFolderCreation.data.unique_id);
    setEditingValue(resHandleFolderCreation.data.name);
    dispatch(fetchMatterDetail(resHandleFolderCreation.data.matter))
    if (resHandleFolderCreation.data.parent) {
      dispatch(fetchMatterDetailList({ id: id, page: 1, folder: `F${resHandleFolderCreation.data.parent}` }))
    } else {
      
      dispatch(fetchMatterDetailList({ id: id, page: 1 }))
    }
      setCurrentPage(1)
      dispatch(setDocListingPage(1));
  }

  const handleFolderCreation = () => {
    const selectedFolderKey = getSelectedFolderKey();
    const currentFolder = selectedFolderKey ? findFolderNode(treeData, selectedFolderKey) : null;
    const currentFileNames = currentFolder
      ? getFolderNamesInFolder(currentFolder)
      : treeData
        .filter(item => item.type === "folder")
        .map(doc => doc.title);
    const newFolderName = getNextUntitledFolderName(currentFileNames);
    const matterIdForCreate = getMatterIdForCreateFolder(currentFolder);
    if (!matterIdForCreate) {
      setErrorToastMsgAndShow("Could not identify this matter. Please refresh the page and try again.");
      return;
    }
    const folderPayload = {
      name: newFolderName,
      matter_id: matterIdForCreate,
      parent_id: currentFolder?.id || selectedFolderKey?.substring(1) || null
    };
    dispatch(createFolders(folderPayload)).unwrap()
      .then((resHandleFolderCreation) => {
        if (resHandleFolderCreation?.status === 201) {
          AfterCreateFolder(resHandleFolderCreation)
        } else {
          // Handle the error message.
          setErrorToastMsgAndShow(resHandleFolderCreation?.payload?.response?.data?.detail || resHandleFolderCreation?.payload?.response?.data?.error || "Folder creation failed");
        }
      }).catch((err) => {
        setErrorToastMsgAndShow(err?.response?.data?.detail || err?.response?.data?.error || "Folder creation failed");
        setisLoading(false);
      })
  }

  useEffect(() => {
  const timeout = setTimeout(() => {
    if (!showTemplatePopup && !showFillModal && !showGeneratedLoader && !showGeneratedSuccess) {
      //clear state when all modals and loaders are closed for good
      setSelectedTemplate(null);
      setSelectedCategory(null);
      setSelectedLetterhead(null);
      setPoaAttorney("");
    }
  }, 1000); // slight delay to handle transition case

  return () => clearTimeout(timeout);
}, [showTemplatePopup, showFillModal, showGeneratedLoader, showGeneratedSuccess]);

  return (
    <ButtonWrapper customwidth={"85px"}>
      {isUploadLoading && <Loader />}

      <SeconadaryButton 
        onClick={(e) => {
          e.stopPropagation();
          setIsMenuOpen(!isMenuOpen);
        }}
        style={{ 
          opacity: 1,
          cursor: 'pointer'
        }}
      >
        Add <CaretDown size={12} />
      </SeconadaryButton>
      {isMenuOpen && (
        <MenuWrapper
          ref={menuRef}
          position={menuPosition}
          top={top}
          right={right}
          width={width}
        >
          {/* Upload File(s) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleUploadFileClick(); // Trigger file input click
              setIsMenuOpen(false);
            }}
            className={`menu-item`}
            style={{ 
              opacity: 1,
              cursor: 'pointer'
            }}
          >
            <div className="btnWrappergap"><FileArrowUp size={16} color="#5A6C87" /> Upload File(s) </div>
          </button>

          {/* Upload Folder(s) */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleUploadFolderClick();
              setIsMenuOpen(false);
            }}
            className={`menu-item`}
            style={{ 
              opacity: 1,
              cursor: 'pointer'
            }}
          >
            <div className="btnWrapper" style={{ gap: "7px", marginLeft: "2px" }}><UploadFoldersIcon /> Upload Folder(s) </div>
          </button>

          {canUseAiUpload && (
            <>
              <HorizondalLine />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  aiFileInputRef.current.click();
                  setIsMenuOpen(false);
                }}
                className={`menu-item`}
                style={{
                  opacity: 1,
                  cursor: 'pointer',
                }}
              >
                <div className="btnWrappergap"><Brain size={16} color="#7C3AED" /> AI Upload </div>
              </button>
            </>
          )}

          <HorizondalLine />

          {/* Create File */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpenCreateFile(!openCreateFile);
            }}
            className={`menu-item`}
          >
            <div className="btnWrappergap"><FilePlus size={16} color="#5A6C87" /> Create File </div>{openCreateFile === true ? <CaretDown size={12} /> : <CaretRight size={12} />}
          </button>

          {openCreateFile === true ?
            <div style={{ marginLeft: '15%', cursor: 'pointer' }}>
              <Box tabIndex={0} className="btnWrappergap-file" style={{ fontSize: '13px', padding: '4%' }} onKeyDown={(e) => { if (e.key === 'Enter') createNewFile('Untitled.docx'); }} onClick={(e) => { createNewFile('Untitled.docx') }}><WordIcon size={16} />Word document</Box>
              <Box tabIndex={0} className="btnWrappergap-file" style={{ fontSize: '13px', padding: '4%' }} onKeyDown={(e) => { if (e.key === 'Enter') createNewPDFFile(); }} onClick={(e) => { createNewPDFFile() }}><PdfIcon size={16} />Pdf</Box>
              <Box tabIndex={0} className="btnWrappergap-file" style={{ fontSize: '13px', padding: '4%' }} onKeyDown={(e) => { if (e.key === 'Enter') createNewFile('Untitled.xlsx'); }} onClick={(e) => { createNewFile('Untitled.xlsx') }}><ExcelIcon size={16} />Excel</Box>
            </div> : <></>
          }

          {/* Create Folder */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleFolderCreation()
              setIsMenuOpen(false);
            }}
            className={`menu-item`}
          >
            <div className="btnWrappergap"><FolderSimplePlus size={16} color="#5A6C87" /> Create Folder </div>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleMenuClick()
            }}
            className={`menu-item`}
          >
            <div className="btnWrappergap"><img src={TemplateIcon} alt="Template" style={{ width: 16, height: 16, marginRight: '0px' }} /> Create file from template </div>
          </button>
        </MenuWrapper>
      )}
      {showTemplatePopup && (
        <SelectTemplatePopup
          onClose={() => {
            setShowTemplatePopup(false);
            setSelectedLetterhead(null);
          }}
          onTemplateSelect={(tpl) => setSelectedTemplate(tpl)}
          onNext={(file, letterhead) => {
            setSelectedLetterhead(letterhead);
            onTemplateNext({
              file,
              setShowTemplatePopup,
              setUploadProgress,
              setShowUploadProgress,
              setSelectedTemplate,
              setFormValues,
              setShowFillModal,
              dispatch,
              fetchTemplateFields,
              animateProgress,
              handleError,
              setIsUploadLoading,
              uploadTemplate,
              selectedTemplate,
              selectedLetterhead: letterhead,
              setErrorToastMsgAndShow,
              matterDetails,
              // Direct-merge dependencies: when the picked template is a fillable PDF,
              // onTemplateNext will skip FillFormFieldsModal and merge immediately using
              // the Salesforce-populated field values, then auto-open the editable PDF
              // in the Matter tab viewer.
              setShowGeneratedLoader,
              setShowGeneratedSuccess,
              fetchMatterDetail,
              fetchMatterDetailList,
              fetchMatterDetailsFolder,
              mergeTemplateFields,
              getSelectedKey,
              treeData,
              id,
              navigate,
              postHighlightedMatter,
            });
          }}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          file={file}
          setFile={setFile}
          showUploadedFile={showUploadedFile}
          setShowUploadedFile={setShowUploadedFile}
          handleError={handleError}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          selectedLetterhead={selectedLetterhead}
          setSelectedLetterhead={setSelectedLetterhead}
          onPackageSelect={(pkg, letterhead) => {
            setSelectedLetterhead(letterhead);
            mergePackageFlow({
              selectedPackage: pkg,
              selectedLetterhead: letterhead,
              matterDetails,
              getSelectedKey,
              findFolderNodeFn: findFolderNode,
              treeData,
              setShowTemplatePopup,
              setShowGeneratedLoader,
              setPackageMergeResult,
              setErrorToastMsgAndShow,
              dispatch,
              fetchMatterDetail,
              fetchMatterDetailList,
              fetchMatterDetailsFolder,
              id,
              mergePackageApi,
            });
          }}
        />
      )}
      <UploadProgress
        open={showUploadProgress}
        onClose={() => setShowUploadProgress(false)}
        percent={uploadProgress}
        title="File is being uploaded into Formstack"
        subtitle="This can take a few seconds"
        hideClose={true}
      />
      <FillFormFieldsModal
        open={showFillModal}
        onClose={() => {
          handleFormFillModalClose(setShowFillModal, showPickerOrLeaveSignatureFlow);
          setSelectedLetterhead(null);
          setPoaAttorney("");
        }}
        matterId={id || matterDetails?.id}
        poaAttorney={poaAttorney}
        onPoaChange={setPoaAttorney}
        formValue={formValues}
        onPreview={() => handlePreviewMergingTemplate({formValues, selectedTemplate, selectedLetterhead, setTemplatePreview, setOpenPreview, setErrorToastMsgAndShow, setShowFillModal, setShowGeneratedLoader, setShowGeneratedSuccess, dispatch, previewTemplateFields, getSelectedKey, findFolderNode, treeData: matterDetails?.treeData || [], templateFields, matterDetails})}
        onBack={() => {
          handleFormFillModalClose(setShowFillModal, showPickerOrLeaveSignatureFlow);
          // Don't clear selectedLetterhead on back - preserve it for when user returns
        }}
        onGenerate={() => {
          console.log('🔘 Generate button clicked', { selectedTemplate, selectedLetterhead, formValues, templateFields });
          if (!selectedTemplate) {
            console.error('❌ selectedTemplate is null when Generate is clicked');
            setErrorToastMsgAndShow("Template information is missing. Please go back and select a template again.");
            return;
          }
          mergeTemplate({formValues, selectedTemplate, selectedLetterhead, setShowFillModal, setShowGeneratedLoader, setShowGeneratedSuccess, setErrorToastMsgAndShow, dispatch, fetchMatterDetail, fetchMatterDetailList, fetchMatterDetailsFolder, mergeTemplateFields, getSelectedKey, findFolderNode, treeData, templateFields, matterDetails, id, poaAttorney, onGenerated: signatureHandoffRef.current});
        }}
        setValues={setFormValues}
        templatePreview={templatePreview}
        openPreview={openPreview}
        setOpenPreview={setOpenPreview}
        handleShowConfirmModal={() => handleShowConfirmModal(setConfirmCloseModal)}
      />
      <MessageModal
        open={showGeneratedSuccess}
        onClose={() => handleGeneratedSuccessClose({setShowGeneratedSuccess, dispatch, setTemplateFields, setMergedDocument, setFormValues, setSelectedTemplate, setShowUploadedFile, setShowGeneratedLoader, setFile, setSelectedLetterhead})}
        title={"File Generated Successfully"}
        message={"Your file is ready. Click below to open and view the file in detail."}
        buttonText={"Open File"}
        icon={<ConfirmationIcon />}
        onClick={() => {
          openFile(mergedDocument, navigate, dispatch, postHighlightedMatter);
          handleGeneratedSuccessClose({setShowGeneratedSuccess, dispatch, setTemplateFields, setMergedDocument, setFormValues, setSelectedTemplate, setShowUploadedFile, setShowGeneratedLoader, setFile, setSelectedLetterhead});
        }}
        variant="confirmation"
      />

      <ConfirmModal
        isOpen={confirmCloseModal}
        title="Warning: This action will be discarded"
        message="This will cancel the template creation process and all progress will be lost. Are you sure you want to proceed?"
        cancelText="Cancel"
        confirmText="Proceed"
        onCancel={() => handleGoBackToFillForm(setConfirmCloseModal, setShowFillModal)}
        onConfirm={() => {
          signatureHandoffRef.current = null;
          signatureBackRef.current = null;
          handleCancelAction(setShowFillModal, setConfirmCloseModal, setFile, setShowUploadedFile);
        }}
      />
      <GeneratingLoader
        open={showGeneratedLoader}
        onClose={() => handleGeneratedSuccessClose({setShowGeneratedSuccess, dispatch, setTemplateFields, setMergedDocument, setFormValues, setSelectedTemplate, setShowUploadedFile, setShowGeneratedLoader, setFile})}
      />

      <PackageMergeSuccessModal
        open={!!packageMergeResult}
        result={packageMergeResult}
        onClose={() => setPackageMergeResult(null)}
        onSendForSignature={(combinedDoc) => {
          setPackageMergeResult(null);
          navigate(
            appendAIPaneTo(
              `/matter${combinedDoc.relative_path}`,
              location.search
            ),
            { state: { document: combinedDoc } }
          );
        }}
      />

      <AIUploadDestinationModal
        open={pendingAIUpload !== null}
        fileCount={pendingAIUpload?.fileCount || 0}
        zipCount={pendingAIUpload?.serverZips?.length || 0}
        treeData={aiUploadTreeData}
        defaultFolderKey={pendingAIUpload?.defaultFolderKey ?? null}
        onConfirm={handleAIDestinationConfirm}
        onCancel={handleAIDestinationCancel}
      />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={UPLOAD_PICKER_ACCEPT}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      <input
        ref={folderInputRef}
        type="file"
        webkitdirectory="true"
        multiple
        style={{ display: "none" }}
        onChange={handleFolderChange}
      />

      <input
        ref={aiFileInputRef}
        type="file"
        multiple
        accept=".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed"
        style={{ display: "none" }}
        onChange={handleAIFileChange}
      />

      {showErrorNotification && (
        <ErrorToast
          message={errorToastMsg}
          onClose={(id) =>
            setErrorToastMsg((prev) => prev.filter((msg) => msg.id !== id))
          } // Hide notification when closed
        />
      )}
    </ButtonWrapper>

  );
});

UploadButton.displayName = "UploadButton";

export default UploadButton;
UploadButton.propTypes = {
  menuPosition: PropTypes.string,
  top: PropTypes.string,
  right: PropTypes.string,
  width: PropTypes.string,
  openNewPDF: PropTypes.func.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  setEditingKey: PropTypes.func.isRequired,
  setEditingValue: PropTypes.func.isRequired,
  setisLoading: PropTypes.func.isRequired,
  treeData: PropTypes.array.isRequired,
  setShowNotification: PropTypes.func,
  setShowENotification: PropTypes.func,
  handleUpload: PropTypes.func,
  matterId: PropTypes.string,
};
